import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Building2,
  ChevronRight,
  Sparkles,
  Loader2,
} from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { SpotlightTour, type SpotlightStep } from "@/components/SpotlightTour";

interface OnboardingTourProps {
  open: boolean;
  currentStep: number;
  onNext: () => void;
  onPrev: () => void;
  onComplete: () => void;
  onSkip: () => void;
}

const industries = [
  { id: "cleaning", label: "Cleaning Services", emoji: "🧹" },
  { id: "lawn_care", label: "Lawn Care / Landscaping", emoji: "🌿" },
  { id: "construction", label: "Construction / Remodeling", emoji: "🔨" },
  { id: "barber", label: "Barbershop", emoji: "💈" },
  { id: "beauty_salon", label: "Beauty Salon / Spa", emoji: "💅" },
  { id: "plumbing", label: "Plumbing", emoji: "🔧" },
  { id: "electrical", label: "Electrical", emoji: "⚡" },
  { id: "hvac", label: "HVAC", emoji: "❄️" },
  { id: "painting", label: "Painting", emoji: "🎨" },
  { id: "roofing", label: "Roofing", emoji: "🏠" },
  { id: "pest_control", label: "Pest Control", emoji: "🐜" },
  { id: "auto_detailing", label: "Auto Detailing", emoji: "🚗" },
  { id: "photography", label: "Photography / Videography", emoji: "📸" },
  { id: "personal_training", label: "Personal Training / Fitness", emoji: "💪" },
  { id: "pressure_washing", label: "Pressure Washing", emoji: "💦" },
  { id: "pool_service", label: "Pool Service", emoji: "🏊" },
  { id: "handyman", label: "Handyman", emoji: "🛠️" },
  { id: "moving", label: "Moving Services", emoji: "📦" },
  { id: "catering", label: "Catering / Events", emoji: "🍽️" },
  { id: "pet_grooming", label: "Pet Grooming", emoji: "🐾" },
  { id: "flooring", label: "Flooring", emoji: "🪵" },
  { id: "interior_design", label: "Interior Design", emoji: "🏡" },
  { id: "tutoring", label: "Tutoring / Education", emoji: "📚" },
  { id: "dj_entertainment", label: "DJ / Entertainment", emoji: "🎵" },
  { id: "welding", label: "Welding / Metalwork", emoji: "🔥" },
  { id: "other", label: "Other Industry", emoji: "🏢" },
];

/**
 * Interactive spotlight steps that highlight actual sidebar items.
 * `target` matches the data-tour attribute on the sidebar link.
 */
const spotlightSteps: SpotlightStep[] = [
  {
    target: "nav-clients",
    route: "/clients",
    title: "👥 Clients",
    description:
      "This is where you manage your clients. Click it to see your client list — add names, emails, phone numbers, and billing addresses.",
    clickToAdvance: true,
  },
  {
    target: "nav-quotes",
    route: "/quotes",
    title: "📝 Quotes",
    description:
      "Create professional quotes with line items, deposits, and payment terms. Send them to clients for online approval and e-signatures.",
    clickToAdvance: true,
  },
  {
    target: "nav-projects",
    route: "/projects",
    title: "📂 Projects",
    description:
      "When a client approves a quote, it automatically becomes a project. Track progress from start to completion right here.",
    clickToAdvance: true,
  },
  {
    target: "nav-invoices",
    route: "/invoices",
    title: "🧾 Invoices",
    description:
      "Bill your clients with professional invoices. Create them from projects or standalone. Track payments and send reminders.",
    clickToAdvance: true,
  },
  {
    target: "nav-appointments",
    route: "/appointments",
    title: "📅 Appointments",
    description:
      "Schedule consultations and work visits. Set up your availability and let clients book online through your booking page.",
    clickToAdvance: true,
  },
  {
    target: "nav-services",
    route: "/services",
    title: "🔧 Services",
    description:
      "Define your service offerings with descriptions and pricing. These can be quickly added as line items on quotes.",
    clickToAdvance: true,
  },
  {
    target: "nav-messages",
    route: "/messages",
    title: "💬 Messages",
    description:
      "Communicate with your clients directly through the app. All conversations are organized and easy to find.",
    clickToAdvance: true,
  },
  {
    target: "nav-settings",
    route: "/settings",
    title: "⚙️ Settings — Important!",
    description:
      "Set up your business name, address, phone, and email here. This is critical — AI-powered features like smart pricing use your location and business details to give accurate results. Don't skip this step!",
    clickToAdvance: true,
  },
];

// Dialog-based steps: 0 = welcome, 1 = industry
// Then spotlight steps: 2..2+N
const DIALOG_STEPS = 2;

export function OnboardingTour({
  open,
  currentStep,
  onNext,
  onPrev,
  onComplete,
  onSkip,
}: OnboardingTourProps) {
  const { organizationId } = useOrganization();
  const queryClient = useQueryClient();

  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null);
  const [isLoadingDemo, setIsLoadingDemo] = useState(false);

  const isDialogPhase = currentStep < DIALOG_STEPS;
  const spotlightIndex = currentStep - DIALOG_STEPS;

  const handleIndustryNext = async () => {
    if (!selectedIndustry || selectedIndustry === "other") {
      onNext();
      return;
    }

    if (!organizationId) {
      onNext();
      return;
    }

    setIsLoadingDemo(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-demo-data", {
        body: { businessType: selectedIndustry, existingOrgId: organizationId },
      });

      if (error) {
        console.error("Demo data error:", error);
        toast.error("Couldn't load sample data, but you can continue setting up manually.");
      } else {
        toast.success("Sample data loaded! Explore your dashboard to see it in action.");
        queryClient.invalidateQueries();
      }
    } catch (err) {
      console.error("Demo data error:", err);
      toast.error("Couldn't load sample data, but you can continue setting up manually.");
    }
    setIsLoadingDemo(false);
    onNext();
  };

  if (!open) return null;

  // ── Spotlight Phase ──
  if (!isDialogPhase) {
    return (
      <SpotlightTour
        steps={spotlightSteps}
        currentStep={spotlightIndex}
        active={true}
        onAdvance={onNext}
        onSkip={onSkip}
        onComplete={onComplete}
      />
    );
  }

  // ── Dialog Phase ──
  if (currentStep === 0) {
    return (
      <Dialog open={true} onOpenChange={(o) => !o && onSkip()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
            <DialogTitle className="text-xl">Welcome to Quote Send Go!</DialogTitle>
            <DialogDescription className="text-base">
              Let's take a quick interactive tour. We'll walk you through each section of the app — just click on the items we highlight!
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <ul className="space-y-3">
              {[
                "Manage quotes, invoices, and projects all in one place",
                "Track client communications and appointments",
                "Get paid faster with integrated payments",
              ].map((detail, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="mt-0.5 h-5 w-5 shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-xs font-medium text-primary">{index + 1}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">{detail}</span>
                </li>
              ))}
            </ul>
          </div>

          <DialogFooter className="flex-row gap-2 sm:justify-between">
            <Button variant="ghost" onClick={onSkip} className="text-muted-foreground">
              Skip tour
            </Button>
            <Button onClick={onNext}>
              Let's Go
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Step 1: Industry selection
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onSkip()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh]">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Building2 className="h-8 w-8 text-primary" />
          </div>
           <DialogTitle className="text-xl">What's your industry?</DialogTitle>
          <DialogDescription className="text-base">
            Select your industry and we'll pre-load your account with <strong>sample data</strong> so you can explore right away. This is just example data to help you see how everything works — you can delete individual items anytime, or remove all sample data at once from <strong>Settings</strong> when you're ready to go live.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="grid grid-cols-2 gap-2 max-h-[340px] overflow-y-auto pr-1">
            {industries.map((ind) => (
              <button
                key={ind.id}
                onClick={() => setSelectedIndustry(ind.id)}
                className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all text-sm ${
                  selectedIndustry === ind.id
                    ? "border-primary bg-primary/10 ring-2 ring-primary/30"
                    : "border-border hover:border-primary/40 hover:bg-muted/50"
                }`}
              >
                <span className="text-xl">{ind.emoji}</span>
                <span className="font-medium text-foreground">{ind.label}</span>
              </button>
            ))}
          </div>
        </div>

        <DialogFooter className="flex-row gap-2 sm:justify-between">
          <Button variant="ghost" onClick={onSkip} className="text-muted-foreground">
            Skip tour
          </Button>
          <div className="flex gap-2">
            {!selectedIndustry && (
              <Button variant="outline" onClick={onNext}>
                Skip this step
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            )}
            {selectedIndustry && (
              <Button onClick={handleIndustryNext} disabled={isLoadingDemo}>
                {isLoadingDemo ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    Load & Continue
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
